import { useEffect } from 'react';

// ── PER-PAGE TITLE, DESCRIPTION, CANONICAL AND ROBOTS ────────────────────────
// This app serves ONE index.html for every route (see public/_redirects), so
// without something like this every URL shares the homepage's title and
// canonical — which tells Google that /designers, /shop and /product/123 are
// all the same document.
//
// ══ WHY NOT react-helmet-async ═══════════════════════════════════════════════
// Because there is no server rendering here. Helmet's real value is emitting
// tags into the HTML a crawler receives; in a client-rendered CRA build it
// runs in an effect after mount, which is exactly what the code below does.
// So it would buy nothing a crawler can see, while adding a dependency and a
// <HelmetProvider> wrapper around the entire app. Fifty lines and no new
// package is the better trade here.
//
// ══ WHAT THIS DOES AND DOES NOT BUY ══════════════════════════════════════════
// Googlebot renders JavaScript, so it does see these. Most social scrapers
// (Facebook, Slack, WhatsApp, LinkedIn) do NOT — they read the raw HTML and
// leave. That is why the defaults in index.html still matter and are written
// to stand on their own, and why og: tags here are a bonus rather than the
// mechanism. Prerendering is what would fix it properly; see the report.

const SITE = 'https://www.tunify.am';

// The values index.html shipped with, captured once before any page has had a
// chance to overwrite them. A page that unmounts restores these, so navigating
// from /shop back to a route that sets nothing cannot leave "Shop" in the tab.
const DEFAULTS = {
  title: typeof document !== 'undefined' ? document.title : '',
  description: readMeta('name', 'description'),
  robots: readMeta('name', 'robots'),
  canonical: readLink('canonical'),
};

function readMeta(attr, key) {
  if (typeof document === 'undefined') return '';
  return document.head.querySelector(`meta[${attr}="${key}"]`)?.getAttribute('content') || '';
}

function readLink(rel) {
  if (typeof document === 'undefined') return '';
  return document.head.querySelector(`link[rel="${rel}"]`)?.getAttribute('href') || '';
}

// Sets a meta tag, creating it if index.html did not ship one. Returns nothing:
// restoring is handled by re-applying DEFAULTS, not by tracking each node.
function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Applies this page's metadata for as long as the component is mounted.
 *
 * @param {object}  opts
 * @param {string}  opts.title        Full <title>. Include the brand yourself.
 * @param {string}  opts.description  One sentence, ~155 chars.
 * @param {string} [opts.path]        Path for the canonical, e.g. '/shop'.
 *                                    Defaults to the current location, which is
 *                                    what dynamic routes want.
 * @param {boolean}[opts.noindex]     Keep it out of the index but still follow
 *                                    its links. For thin or duplicate pages.
 * @param {string} [opts.image]       Absolute URL for og:image, if this page has
 *                                    a better one than the site default.
 */
export function useSeo({ title, description, path, noindex = false, image } = {}) {
  useEffect(() => {
    // A dynamic page renders once with no data and again with it. Skipping the
    // empty first pass stops the tab flashing "undefined" and stops a canonical
    // being written from a half-built URL.
    if (!title && !description) return undefined;

    const canonical = SITE + (path || window.location.pathname);

    if (title) document.title = title;
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (image) {
      setMeta('property', 'og:image', image);
      setMeta('name', 'twitter:image', image);
    }

    setCanonical(canonical);
    setMeta('property', 'og:url', canonical);
    setMeta('name', 'robots',
      noindex
        // follow, so the crawler still walks through to the profiles and
        // listings this page links to — a noindex,nofollow would strand them.
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    return () => {
      document.title = DEFAULTS.title;
      setMeta('name', 'description', DEFAULTS.description);
      setMeta('name', 'robots', DEFAULTS.robots);
      setCanonical(DEFAULTS.canonical);
    };
  }, [title, description, path, noindex, image]);
}

export default useSeo;
